"use client";
import { StarFilledIcon } from "@radix-ui/react-icons";
import constants from "../constants/constants";
import React from "react";
import { useUserParams } from "../hooks/useUserParams";

// Highlight component for styling highlighted text
const Highlight = ({ children, className = "" }) => {
  return (
    <span
      className={`bg-cyan-600/20 p-1 py-0.5 font-bold text-cyan-600 dark:bg-cyan-600/20 dark:text-cyan-600 ${className}`}
    >
      {children}
    </span>
  );
};

// Parse description from MongoDB format: ["text", {highlight: "text"}, "text"]
// Converts array format to React elements with highlighted text
// Also handles React elements directly (from constants.js)
const parseDescription = (description) => {
  if (!description) {
    return "";
  }

  // If description is a string, return as is
  if (typeof description === "string") {
    return description;
  }

  // If description is already a React element (from constants.js), return it directly
  // React elements have $$typeof property
  if (description && typeof description === "object" && description.$$typeof) {
    return description;
  }

  // Array format: ["text", {highlight: "text"}, "text"]
  if (Array.isArray(description)) {
    const parts = description
      .map((part, index) => {
        if (typeof part === "string") {
          return part;
        }
        if (part && typeof part === "object" && part.highlight && typeof part.highlight === "string") {
          return React.createElement(Highlight, { key: `highlight-${index}` }, part.highlight);
        }
        return null;
      })
      .filter((part) => part !== null);

    if (parts.length === 0) {
      return "";
    }

    if (parts.length === 1) {
      return parts[0];
    }

    return React.createElement("p", { key: "desc" }, ...parts);
  }

  return "";
};

export function TestimonialCard({
  description,
  name,
  role,
  className,
  ...props // Capture the rest of the props
}) {
  const parsedDescription = React.useMemo(() => {
    return parseDescription(description);
  }, [description]);

  return (
    <div
      className="flex w-72 shrink-0 cursor-pointer snap-center snap-always flex-col justify-between rounded-xl p-4 shadow-xl shadow-black/[0.1] lg:min-w-96 border border-neutral-200 bg-white"
      {...props} // Spread the rest of the props here
    >
      <div className="select-none font-normal text-neutral-700 dark:text-neutral-400">
        {parsedDescription}
      </div>

      <div className="select-none">
        <div className="flex flex-row py-1">
          <StarFilledIcon className="size-4 text-yellow-500" />
          <StarFilledIcon className="size-4 text-yellow-500" />
          <StarFilledIcon className="size-4 text-yellow-500" />
          <StarFilledIcon className="size-4 text-yellow-500" />
          <StarFilledIcon className="size-4 text-yellow-500" />
        </div>
        <p className="font-medium text-neutral-500">{name}</p>
        <p className="text-sm font-normal text-neutral-400">{role}</p>
      </div>
    </div>
  );
}

const AUTO_SCROLL_DELAY = 2000;
const INIT_DELAY = 100;
const START_DELAY = 500;
const USER_SCROLL_RESET_DELAY = 150;
const GAP_FALLBACK = 24;

export default function Testimonials() {
  const { getCurrentTestimonials } = useUserParams();
  const scrollRef = React.useRef(null);
  const currentIndexRef = React.useRef(0);
  const timeoutRef = React.useRef(null);
  const userScrollTimeoutRef = React.useRef(null);
  const rawTestimonials = getCurrentTestimonials();
  const testimonials = React.useMemo(() => {
    if (Array.isArray(rawTestimonials) && rawTestimonials.length > 0) {
      return rawTestimonials;
    }
    if (typeof rawTestimonials === "string") {
      try {
        const parsed = JSON.parse(rawTestimonials);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      } catch (error) {
        console.warn("Unable to parse testimonials string", error);
      }
    }
    if (rawTestimonials && typeof rawTestimonials === "object") {
      const values = Object.values(rawTestimonials);
      if (values.length > 0) {
        return values;
      }
    }
    // Fallback to constants if no testimonials from hook
    return constants.TESTIMONIALS || [];
  }, [rawTestimonials]);

  const normalizeTestimonial = React.useCallback((testimonial, index) => {
    if (testimonial == null) {
      return {
        description: "",
        name: `Testimonial ${index + 1}`,
        role: "",
      };
    }

    if (typeof testimonial === "string") {
      return {
        description: testimonial,
        name: `Testimonial ${index + 1}`,
        role: "",
      };
    }

    if (typeof testimonial === "object") {
      return {
        ...testimonial,
        description: testimonial.description ?? testimonial.quote ?? "",
        name: testimonial.name ?? `Testimonial ${index + 1}`,
        role: testimonial.role ?? testimonial.title ?? "",
      };
    }

    return {
      description: "",
      name: `Testimonial ${index + 1}`,
      role: "",
    };
  }, []);

  React.useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const getCards = () =>
      Array.from(container.querySelectorAll("[data-testimonial-card]"));

    const getCardWidth = () => {
      const cards = getCards();
      if (!cards.length) return 0;
      const gap = parseFloat(getComputedStyle(container).gap) || GAP_FALLBACK;
      return cards[0].offsetWidth + gap;
    };

    let isHovering = false;
    let isUserScrolling = false;
    let isAutoScrolling = false;

    const scrollToIndex = (index) => {
      const cards = getCards();
      const cardWidth = getCardWidth();
      if (!cards.length || !cardWidth) return;

      container.scrollTo({
        left: (index + 1) * cardWidth,
        behavior: "smooth",
      });
    };

    const updateCurrentIndex = () => {
      const cards = getCards();
      const cardWidth = getCardWidth();
      if (!cards.length || !cardWidth) return;

      const scrollIndex = Math.round(
        (container.scrollLeft - cardWidth) / cardWidth
      );
      if (scrollIndex >= 0 && scrollIndex < cards.length) {
        currentIndexRef.current = scrollIndex;
      }
    };

    const scheduleNext = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        if (isHovering || isUserScrolling) {
          scheduleNext();
          return;
        }

        const cards = getCards();
        if (!cards.length) {
          scheduleNext();
          return;
        }

        currentIndexRef.current =
          (currentIndexRef.current + 1) % cards.length;
        isAutoScrolling = true;
        scrollToIndex(currentIndexRef.current);

        setTimeout(() => {
          isAutoScrolling = false;
          scheduleNext();
        }, AUTO_SCROLL_DELAY);
      }, 0);
    };

    // Initialize scroll position
    const initTimeout = setTimeout(() => {
      const cardWidth = getCardWidth();
      if (cardWidth > 0) {
        container.scrollLeft = cardWidth;
        currentIndexRef.current = 0;
      }
    }, INIT_DELAY);

    const startTimeout = setTimeout(scheduleNext, START_DELAY);

    // Event handlers
    const handleMouseEnter = () => {
      isHovering = true;
    };

    const handleMouseLeave = () => {
      isHovering = false;
    };

    const handleScroll = () => {
      if (!isAutoScrolling && !isUserScrolling) {
        isUserScrolling = true;
        if (userScrollTimeoutRef.current)
          clearTimeout(userScrollTimeoutRef.current);
        userScrollTimeoutRef.current = setTimeout(() => {
          isUserScrolling = false;
        }, USER_SCROLL_RESET_DELAY);
      }
      updateCurrentIndex();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      } else {
        scheduleNext();
      }
    };

    container.addEventListener("mouseenter", handleMouseEnter);
    container.addEventListener("mouseleave", handleMouseLeave);
    container.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(initTimeout);
      clearTimeout(startTimeout);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (userScrollTimeoutRef.current)
        clearTimeout(userScrollTimeoutRef.current);
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
      container.removeEventListener("scroll", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <section id="testimonials">
      <div className="py-14">
        <div className="mx-auto md:container md:px-8">
          <h1 className="text-5xl text-ocean py-8 font-mont text-center mt-6 font-bold">
            TESTIMONIALS
          </h1>
          <div className="relative mx-auto mt-6 max-w-[100vw] overflow-hidden">
            <div
              ref={scrollRef}
              className="flex w-full snap-x snap-mandatory flex-row gap-6 overflow-x-auto py-14 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {/* Empty placeholder  */}
              <div className="size-72 shrink-0 md:h-60 md:min-w-96"></div>
              {testimonials.map((testimonial, idx) => {
                const normalized = normalizeTestimonial(testimonial, idx);
                return (
                  <TestimonialCard
                    {...normalized}
                    key={idx}
                    data-testimonial-card
                    tabIndex={0}
                  />
                );
              })}
              {/* Empty placeholder  */}
              <div className="size-72 shrink-0 md:h-60 md:min-w-96"></div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 hidden h-full w-1/5 bg-gradient-to-r from-background md:block"></div>
            <div className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-1/5 bg-gradient-to-l from-background md:block"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
